<script setup>
/* Imports */
import { computed } from 'vue';
import { EmsOptionPicker, useProductOptions } from '@garmin/ui-product-ems-filters';

/* Analytics */
import {
  sendAnalyticsEvent,
  sanitizeAnalyticsString,
  getCategory3Data,
  getAnalyticsPriceData,
} from '../helpers/ga/analytics.js';

/* Helpers */
import {
  mapGetters,
  mapActions,
  buildProductPageUrl,
  getProductPageUrl,
  announceAccessibilityMessage,
} from '../helpers/mainHelpers.js';

/* Composables */
import { useGenericAnalyticsDataHelper } from '../composables/useAnalyticsData.js';
import { useHandleAffirmUpdate } from '../composables/useHandleAffirmUpdate.js';

/* Initialize Composables */
const productOptions = useProductOptions();
const { genericAnalyticsData } = useGenericAnalyticsDataHelper();
const { handleAffirmUpdate } = useHandleAffirmUpdate();

/* Store Getters and Actions */
const {
  getGlobalProductName,
  getLocale,
  getPartNumber,
  getProductBySku,
  getTranslations,
} = mapGetters();

const { updateProductData } = mapActions();

/* Computed */
const filterDropdownList = computed(() => productOptions.filterDropdownList.value);
const optionsList = computed(() => {
  if (Array.isArray(filterDropdownList.value) && filterDropdownList.value?.length > 1) {
    return productOptions.optionsList.value;
  }
  return productOptions.getOptionListByType('ems');
});
const optionText = computed(() => {
  const productDetails = getProductBySku.value(getPartNumber.value);
  return productDetails?.productVariation ?? '';
});

const optionPickerTranslations = computed(() => ({
  NOT_AVAILABLE_WITH_SELECTION_TEXT: getTranslations.value?.ITFE_PRODUCT_PAGES_NOT_AVAILABLE_WITH_SELECTION_TEXT || '',
}));

/* Methods */
const getUrl = (item) => buildProductPageUrl(item, {
  locale: getLocale.value ?? '',
  origin: window?.location?.origin,
});
const getAnalyticsProductName = () => sanitizeAnalyticsString(getGlobalProductName.value) || 'NA';
const getCategory3Value = () => {
  const productName = getAnalyticsProductName();
  return getCategory3Data(productName);
};

const sendProductVariationEvent = (sku) => {
  if (!sku) return;

  const selectedProduct = getProductBySku.value(sku);

  const productPrice = {
    priceObj: {
      listPrice: selectedProduct?.price?.listPrice,
      salePrice: selectedProduct?.price?.salePrice,
    },
    sellable: selectedProduct?.isSellable,
    showPrice: selectedProduct?.showPrice,
  };

  const selectedProductName = sanitizeAnalyticsString(selectedProduct?.globalProductName);
  const selectedProductIndex = productOptions.getOptionListByType('ems')[0]?.options?.findIndex((option) => option.sku === sku);
  const productCategory4 = productOptions.getOptionListByType('ems')[0]?.options?.find((option) => option.sku === sku)?.text || 'NA';
  const productCategory5 = productOptions.getOptionListByType('image')[0]?.options?.find((option) => option.sku === sku)?.text || 'NA';

  const analyticsData = {
    ...genericAnalyticsData(),
    // Send the following fields as 'NA' for now since they are not available in the current context
    creative_name: 'NA',
    creative_slot: 'NA',
    location_id: 'NA',
    promotion_name: 'NA',
    promotion_id: 'NA',
    bundle_id: 'NA',
    is_bundle: 'NA',
    sale_type: 'NA',
    item_list_id: ['Product Page EMS'],
    item_list_name: [`${selectedProductName} - Product Page EMS - Button`],
    index: [selectedProductIndex + 1],
    product_id: selectedProduct?.productId ? [selectedProduct?.productId] : ['NA'],
    product_brand: window?.utag_data?.product_brand || ['NA'],
    product_category: window?.utag_data?.product_category || ['NA'],
    product_category2: window?.utag_data?.product_category2 || ['NA'],
    product_category3: getCategory3Value(),
    product_category4: [productCategory4],
    product_category5: [productCategory5],
    product_variant: selectedProduct?.productVariation ? [sanitizeAnalyticsString(selectedProduct?.productVariation)] : ['NA'],
    product_name: selectedProduct?.globalProductName ? [selectedProductName] : ['NA'],
    product_original_price: [getAnalyticsPriceData(productPrice).listPrice],
    product_price: [getAnalyticsPriceData(productPrice).price],
    product_sku: sku ? [sku] : ['NA'],
    product_url: window?.location?.href ? [window.location.href.split('?')[0]] : ['NA'],
    product_availability: [getAnalyticsPriceData(productPrice).availability],
    item_family: getCategory3Value(),
    is_sale: getAnalyticsPriceData(productPrice).isSale,
    in_stock: getAnalyticsPriceData(productPrice).inStock,
    price_shown: getAnalyticsPriceData(productPrice).priceShown,
    link_type: ['EMS button'],
    tealium_event: 'select_item',
  };

  sendAnalyticsEvent(analyticsData);
};

const changeOption = async (option) => {
  const altText = option?.altText || option.text;

  if (altText) {
    // Accessibility announcement
    announceAccessibilityMessage(altText);
  }

  // Send analytics event
  sendProductVariationEvent(option?.sku);

  // Retrieve the updated product data from Store
  const selectedProduct = await getProductBySku.value(option?.sku);

  // Update Affirm promotions with the new price
  await handleAffirmUpdate(selectedProduct);

  // Update the product options composable BEFORE updating the store so that
  // the `getPartNumber` watcher in ProductPage.vue (which fires during
  // `updateProductData` and dispatches page_view / view_item) reads the fresh
  // options for the newly selected SKU — otherwise the resulting analytics
  // fields such as `index` and `product_category4` are computed from the
  // previous SKU's options.
  productOptions.setFilterDropdownList(window?.emsData?.filterDropDown ?? []);
  productOptions.setProductOptions({
    optionsList: window?.emsData?.emsDetails ?? [],
    options: window?.emsData?.skuVariantMatrix?.[option?.sku] ?? {},
  });

  // Update the product data in the store and trigger a re-render of the product page with the new product data
  await updateProductData(option?.sku);
};

const changeDropdown = async ({ event, selectedOption } = {}) => {
  const url = event?.target?.value;
  const sku = selectedOption?.sku;
  // If a sku is not found in url, redirect the page to the url
  // This is fallback in case the dropdown value provided doesn't have the sku (bad data)
  if (!sku) {
    window.location.assign(getProductPageUrl(url, window?.location?.origin));
    return;
  }

  // Send analytics event
  sendProductVariationEvent(sku);

  const selectedProduct = await getProductBySku.value(sku);

  // Update Affirm promotions with the new price
  await handleAffirmUpdate(selectedProduct);

  // Update the product options composable BEFORE updating the store so that
  // the `getPartNumber` watcher in ProductPage.vue (which fires during
  // `updateProductData` and dispatches page_view / view_item) reads the fresh
  // options for the newly selected SKU — otherwise the resulting analytics
  // fields such as `index` and `product_category4` are computed from the
  // previous SKU's options.
  productOptions.setFilterDropdownList(window?.emsData?.filterDropDown ?? []);
  // Mark the newly selected dropdown option after refreshing the list so the
  // selection state isn't overwritten by the static emsData values.
  productOptions.setFilterDropdownSelected(sku);
  productOptions.setProductOptions({
    optionsList: window?.emsData?.emsDetails ?? [],
    options: window?.emsData?.skuVariantMatrix?.[sku] ?? {},
  });

  // Update the product data in the store and trigger a re-render of the product page with the new product data
  await updateProductData(sku);
};
</script>

<template>
  <EmsOptionPicker
    :options-list="optionsList"
    :option-text="optionText"
    :filter-dropdown-list="filterDropdownList"
    :format-url="getUrl"
    :translations="optionPickerTranslations"
    @ems-options-change-dropdown="changeDropdown($event)"
    @ems-options-change-option="changeOption($event)"
  />
</template>
